import { usePostHog } from "@posthog/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import z from "zod/v4";

import { authClient } from "@/auth/client";
import { useAppForm } from "@/components/base/form";
import { useLoggedInRedirect } from "@/hooks/auth";
import { useTRPC } from "@/integrations/trpc/react";
import { normalizePhone } from "@/lib/phone";
import type { FileRouteTypes } from "@/routeTree.gen";

import { Link } from "../base/link";

export type LoginFormProps = {
  className?: string;
  next?: FileRouteTypes["to"];
};

const schema = z.object({
  identifier: z.string().nonempty({ message: "This field is required" }),
  password: z.string().nonempty({ message: "Password is required" }),
});

/**
 * Detect the type of identifier entered.
 * - Contains @ → email
 * - Normalizes to valid E.164 phone → phone
 * - Otherwise → username
 */
function detectIdentifierType(value: string): "email" | "phone" | "username" {
  if (value.includes("@")) {
    return "email";
  }

  // Check if it looks like a phone number
  const normalized = normalizePhone(value);
  if (normalized) {
    return "phone";
  }

  return "username";
}

export function LoginForm({ className, next }: LoginFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const posthog = usePostHog();
  const redirectTo = next ?? "/account";

  useLoggedInRedirect(redirectTo);

  const { mutate: login, failureReason } = useMutation({
    mutationFn: async ({ identifier, password }: z.infer<typeof schema>) => {
      const identifierType = detectIdentifierType(identifier);

      if (identifierType === "email") {
        const { data, error } = await authClient.signIn.email({
          email: identifier,
          password,
        });
        if (error) throw error;
        return { user: data.user, identifierType };
      } else if (identifierType === "phone") {
        const phoneNumber = normalizePhone(identifier);
        if (!phoneNumber) throw new Error("Invalid phone number");
        const { data, error } = await authClient.signIn.phoneNumber({
          phoneNumber,
          password,
        });
        if (error) throw error;
        return { user: data.user, identifierType };
      }
      const { data, error } = await authClient.signIn.username({
        username: identifier,
        password,
      });
      if (error) throw error;
      return { user: data.user, identifierType };
    },
    onSuccess: async ({ user, identifierType }) => {
      posthog.identify(user.id, {
        email: user.email,
        identifier_type: identifierType,
      });
      posthog.capture("user_logged_in", {
        identifier_type: identifierType,
      });

      await queryClient.invalidateQueries({
        queryKey: trpc.viewer.get.queryOptions().queryKey,
      });
      navigate({ to: redirectTo });
    },
  });

  const form = useAppForm({
    defaultValues: { identifier: "", password: "" },
    validators: { onMount: schema, onChange: schema },
    onSubmit: ({ value }) => login(value),
  });

  return (
    <form
      className={className}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className="flex max-w-md flex-col gap-4">
        {failureReason && (
          <form.Alert
            color="error"
            title="Uh oh!"
            description={failureReason.message}
          />
        )}

        <form.AppField
          name="identifier"
          children={(field) => (
            <field.Text
              isRequired
              label="Email, phone, or username"
              placeholder="Enter your email, phone, or username"
              field={field}
            />
          )}
        />

        <form.AppField
          name="password"
          children={(field) => (
            <field.Password
              isRequired
              label="Password"
              labelRight={
                <Link to="/account/forgot-password" className="hover:underline">
                  Forgot your password?
                </Link>
              }
              placeholder="Enter your password"
              field={field}
            />
          )}
        />

        <form.AppForm>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <form.Footer>
                <form.SubmitButton
                  className="w-full"
                  isDisabled={Boolean(!canSubmit || isSubmitting)}
                >
                  Log in
                </form.SubmitButton>
              </form.Footer>
            )}
          />
        </form.AppForm>
      </div>
    </form>
  );
}
